import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { MultiColumnVizProps, useChartTheme } from './types';
import { dsVar } from '../../theme/tokens';
import { tableCellPadSx, tableCellTypeSx, tableHeadTypeSx } from '../../theme/dataTable';

/**
 * Renders a two-column side-by-side table for sentiment-split data.
 * The first column gets a green left border (positive),
 * the second gets a red left border (negative).
 *
 * Triggered by: visualizationType = 'SENTIMENT_SPLIT_TABLE'
 */
const SentimentSplitTable: React.FC<MultiColumnVizProps> = ({
  headers,
  data,
  safeValue,
}) => {
    const theme = useChartTheme();
  const sentimentColors = [theme.sentiment.positive, theme.sentiment.negative];

  return (
    <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      {headers.map((header, colIdx) => (
        <TableContainer
          key={colIdx}
          component={Paper}
          sx={{
            backgroundColor: dsVar('surface'),
            border: `1px solid ${dsVar('line')}`,
            borderRadius: '12px',
            boxShadow: 'none',
            borderLeft: `4px solid ${sentimentColors[colIdx] ?? dsVar('line')}`,
          }}
        >
          <Table size="small">
            <TableHead sx={{ backgroundColor: dsVar('raised') }}>
              <TableRow>
                <TableCell
                  sx={{
                    ...tableHeadTypeSx,
                    ...tableCellPadSx,
                    borderBottom: `1px solid ${dsVar('line')}`,
                  }}
                >
                  {header}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, rowIdx) => (
                <TableRow
                  key={rowIdx}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell sx={{ ...tableCellTypeSx, ...tableCellPadSx, borderBottom: `1px solid ${dsVar('line')}` }}>
                    {safeValue(
                      Array.isArray(row)
                        ? row[colIdx]
                        : (row as any)[header] ||
                          (row as any)[header.toLowerCase()]
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}
    </div>
  );
};

export default SentimentSplitTable;
